import {
  RATE_BASIS_SORT_ORDER,
  RATE_BASIS_UNCONFIGURED,
  buildServiceTagMetaByName,
  getRateBasisLabel,
  getRateBasisShortLabel,
  normalizeTagName,
} from '../serviceTagRegistry';
import { normalizeLower } from './serviceNormalization';

const initStatusCounts = () => ({ pending: 0, done: 0, paid: 0 });

const applyStatusCount = (target, status) => {
  if (status === 'done') target.done += 1;
  else if (status === 'paid') target.paid += 1;
  else target.pending += 1;
};

export const getServiceTopSummary = (jobs = []) => {
  const statusCounts = initStatusCounts();
  let totalInternalCost = 0;
  let totalCustomerServiceCharge = 0;
  let totalVendorPayable = 0;
  let invoiceVisibleServiceRevenue = 0;

  jobs.forEach((job) => {
    applyStatusCount(statusCounts, normalizeLower(job.status));
    totalInternalCost += job.internalCost || 0;
    totalCustomerServiceCharge += job.customerCharge || 0;
    if (normalizeLower(job.status) !== 'paid') totalVendorPayable += job.internalCost || 0;
    if (job.showOnInvoice) invoiceVisibleServiceRevenue += job.customerCharge || 0;
  });

  return {
    totalServiceJobs: jobs.length,
    pendingJobs: statusCounts.pending,
    doneJobs: statusCounts.done,
    paidJobs: statusCounts.paid,
    totalInternalCost,
    totalCustomerServiceCharge,
    totalVendorPayable,
    invoiceVisibleServiceRevenue,
  };
};

export const getTagWiseAggregation = (jobs = []) => {
  const map = new Map();
  jobs.forEach((job) => {
    const key = normalizeLower(job.serviceName) || 'unclassified';
    if (!map.has(key)) {
      map.set(key, {
        tag: key,
        usageCount: 0,
        pendingCount: 0,
        doneCount: 0,
        paidCount: 0,
        totalInternalCost: 0,
        totalCustomerCharge: 0,
        invoiceVisibleCharge: 0,
        uniqueVendorsUsed: 0,
        averageInternalCost: 0,
        averageCustomerCharge: 0,
        _vendors: new Set(),
      });
    }
    const row = map.get(key);
    row.usageCount += 1;
    applyStatusCount(row, normalizeLower(job.status));
    row.totalInternalCost += job.internalCost || 0;
    row.totalCustomerCharge += job.customerCharge || 0;
    if (job.showOnInvoice) row.invoiceVisibleCharge += job.customerCharge || 0;
    if (job.vendorId || job.vendorName) row._vendors.add(normalizeLower(job.vendorId || job.vendorName));
  });

  return Array.from(map.values()).map((row) => {
    row.uniqueVendorsUsed = row._vendors.size;
    row.averageInternalCost = row.usageCount ? row.totalInternalCost / row.usageCount : 0;
    row.averageCustomerCharge = row.usageCount ? row.totalCustomerCharge / row.usageCount : 0;
    delete row._vendors;
    return row;
  });
};

/** Attach rate-basis classification from serviceTags master for reports. */
export const enrichTagWiseRowsWithMeta = (rows = [], serviceTagMaster = []) => {
  const metaByName = buildServiceTagMetaByName(serviceTagMaster);
  return rows.map((row) => {
    const meta = metaByName.get(normalizeTagName(row.tag));
    const rateBasis = meta?.rateBasis || RATE_BASIS_UNCONFIGURED;
    const defaultRate = parseFloat(meta?.defaultRate);
    const hasDefaultRate = Number.isFinite(defaultRate) && defaultRate > 0;
    const averageInternalCost = row.averageInternalCost || 0;
    return {
      ...row,
      rateBasis,
      rateBasisLabel: getRateBasisLabel(rateBasis),
      rateBasisShortLabel: getRateBasisShortLabel(rateBasis),
      defaultRate: hasDefaultRate ? defaultRate : null,
      avgInternalPerJob: averageInternalCost,
      costVsDefaultRate: hasDefaultRate ? averageInternalCost - defaultRate : null,
    };
  });
};

export const groupTagWiseRowsByRateBasis = (enrichedRows = []) => {
  const groupMap = new Map();
  enrichedRows.forEach((row) => {
    const key = row.rateBasis || RATE_BASIS_UNCONFIGURED;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        rateBasis: key,
        rateBasisLabel: row.rateBasisLabel || getRateBasisLabel(key),
        rateBasisShortLabel: row.rateBasisShortLabel || getRateBasisShortLabel(key),
        rows: [],
        usageCount: 0,
        pendingCount: 0,
        doneCount: 0,
        paidCount: 0,
        totalInternalCost: 0,
        totalCustomerCharge: 0,
      });
    }
    const group = groupMap.get(key);
    group.rows.push(row);
    group.usageCount += row.usageCount || 0;
    group.pendingCount += row.pendingCount || 0;
    group.doneCount += row.doneCount || 0;
    group.paidCount += row.paidCount || 0;
    group.totalInternalCost += row.totalInternalCost || 0;
    group.totalCustomerCharge += row.totalCustomerCharge || 0;
  });

  groupMap.forEach((group) => {
    group.rows.sort((a, b) => String(a.tag).localeCompare(String(b.tag)));
  });

  return RATE_BASIS_SORT_ORDER
    .filter((basis) => groupMap.has(basis))
    .map((basis) => groupMap.get(basis))
    .concat(
      Array.from(groupMap.keys())
        .filter((k) => !RATE_BASIS_SORT_ORDER.includes(k))
        .map((k) => groupMap.get(k))
    );
};

export const buildTagFilterGroups = (tagNames = [], serviceTagMaster = []) => {
  const metaByName = buildServiceTagMetaByName(serviceTagMaster);
  const groupMap = new Map();
  const uniqueTags = Array.from(new Set(tagNames.map((t) => normalizeTagName(t)).filter(Boolean))).sort();

  uniqueTags.forEach((tag) => {
    const meta = metaByName.get(tag);
    const rateBasis = meta?.rateBasis || RATE_BASIS_UNCONFIGURED;
    if (!groupMap.has(rateBasis)) {
      groupMap.set(rateBasis, {
        rateBasis,
        label: getRateBasisLabel(rateBasis),
        tags: [],
      });
    }
    groupMap.get(rateBasis).tags.push(tag);
  });

  return RATE_BASIS_SORT_ORDER
    .filter((basis) => groupMap.has(basis))
    .map((basis) => groupMap.get(basis))
    .concat(
      Array.from(groupMap.keys())
        .filter((k) => !RATE_BASIS_SORT_ORDER.includes(k))
        .map((k) => groupMap.get(k))
    );
};

export const getVendorWiseAggregation = (jobs = []) => {
  const map = new Map();
  jobs.forEach((job) => {
    const key = normalizeLower(job.vendorId || job.vendorName) || 'unassigned';
    const label = job.vendorName || job.vendorId || 'Unassigned';
    if (!map.has(key)) {
      map.set(key, {
        vendorKey: key,
        vendorName: label,
        totalJobs: 0,
        pendingJobs: 0,
        doneJobs: 0,
        paidJobs: 0,
        totalInternalCost: 0,
        pendingPayable: 0,
        paidAmount: 0,
        tagsHandled: [],
        averageCostPerJob: 0,
        lastJobDate: null,
        _tags: new Set(),
        _jobs: [],
      });
    }
    const row = map.get(key);
    row.totalJobs += 1;
    applyStatusCount(row, normalizeLower(job.status));
    row.totalInternalCost += job.internalCost || 0;
    if (normalizeLower(job.status) === 'paid') row.paidAmount += job.internalCost || 0;
    else row.pendingPayable += job.internalCost || 0;
    if (job.serviceName) row._tags.add(normalizeLower(job.serviceName));
    if (job.billDate && (!row.lastJobDate || new Date(job.billDate) > new Date(row.lastJobDate))) {
      row.lastJobDate = job.billDate;
    }
    row._jobs.push({
      billId: job.billId,
      billNo: job.billNo,
      billDate: job.billDate,
      partyName: job.partyName,
      itemName: job.itemName,
      serviceName: job.serviceName,
      measurementLabel: job.measurementLabel || '',
      status: normalizeLower(job.status) || 'pending',
      internalCost: job.internalCost || 0,
      customerCharge: job.customerCharge || 0,
      showOnInvoice: Boolean(job.showOnInvoice),
    });
  });

  return Array.from(map.values()).map((row) => {
    row.tagsHandled = Array.from(row._tags);
    row.averageCostPerJob = row.totalJobs ? row.totalInternalCost / row.totalJobs : 0;
    row.jobDetails = (row._jobs || []).sort(
      (a, b) => new Date(b.billDate || 0).getTime() - new Date(a.billDate || 0).getTime()
    );
    delete row._tags;
    delete row._jobs;
    return row;
  });
};

export const getVendorTagMatrix = (jobs = []) => {
  const map = new Map();
  jobs.forEach((job) => {
    const vendorKey = normalizeLower(job.vendorId || job.vendorName) || 'unassigned';
    const vendorName = job.vendorName || job.vendorId || 'Unassigned';
    const tag = normalizeLower(job.serviceName) || 'unclassified';
    const key = `${vendorKey}::${tag}`;
    if (!map.has(key)) {
      map.set(key, {
        vendorKey,
        vendorName,
        tag,
        jobCount: 0,
        pendingCount: 0,
        totalCost: 0,
        paidCost: 0,
        unpaidCost: 0,
      });
    }
    const row = map.get(key);
    row.jobCount += 1;
    if (normalizeLower(job.status) !== 'paid') row.pendingCount += 1;
    row.totalCost += job.internalCost || 0;
    if (normalizeLower(job.status) === 'paid') row.paidCost += job.internalCost || 0;
    else row.unpaidCost += job.internalCost || 0;
  });

  return Array.from(map.values());
};
